"use client";

import { useState, useMemo, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  VehicleType,
  VehicleMakeOutput,
  VehicleModelOutput,
  CustomerType,
  CustomerCategory,
  CustomerStatus,
} from "@/types";
import { ServiceProvider } from "@/types";
import { toast } from "sonner";
import { makeService } from "@/services/vehicle/makeService";
import { modelService } from "@/services/vehicle/modelService";
import { serviceProviderService } from "@/services/serviceProviderService";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RepeatedAppointmentSetup } from "./RepeatedAppointmentSetup";
import { Vehicle } from "@/types";
import { useApp } from "@/context/AppContext";

export interface CustomerFormData {
  name: string;
  phone: string;
  email?: string;
  address?: string;
  city?: string;
  notes?: string;
  customerType?: CustomerType;
  customerCategory?: CustomerCategory;
  customerStatus?: CustomerStatus;
  serviceProviderId?: string;
  serviceProviders?: string[];
  // Vehicle fields (optional, only used when creating customer)
  vehiclePlateNumber?: string;
  makeName?: string;
  modelName?: string;
  vehicleYear?: number;
  vehicleColor?: string;
  vehicleType?: VehicleType;
}

interface CustomerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formData: CustomerFormData;
  onFormDataChange?: (data: CustomerFormData) => void;
  onSave: (data: CustomerFormData) => void;
  onCancel: () => void;
  mode: "add" | "edit";
  makes: VehicleMakeOutput[];
  models: VehicleModelOutput[];
  onMakesRefresh: () => Promise<void>;
  onModelsRefresh: () => Promise<void>;
  phoneError?: string;
  onPhoneErrorClear?: () => void;
  // For edit mode - to display recurring appointments
  customerId?: string;
  customerVehicles?: Vehicle[];
}

export function CustomerFormDialog({
  open,
  onOpenChange,
  formData: initialFormData,
  onFormDataChange,
  onSave,
  onCancel,
  mode,
  makes,
  models,
  onMakesRefresh,
  onModelsRefresh,
  phoneError,
  onPhoneErrorClear,
  customerId,
  customerVehicles,
}: CustomerFormDialogProps) {
  // Get packages from context
  const { oneTimePackages } = useApp();

  // Manage form data internally to prevent parent re-renders
  const [formData, setFormData] = useState<CustomerFormData>(initialFormData);
  const prevOpenRef = useRef(open);

  // Sync with parent only when dialog opens (not on every initialFormData change)
  useEffect(() => {
    // Only sync when dialog transitions from closed to open
    if (open && !prevOpenRef.current) {
      setFormData(initialFormData);
    }
    prevOpenRef.current = open;
  }, [open, initialFormData]);

  const [makeSearchTerm, setMakeSearchTerm] = useState("");
  const [modelSearchTerm, setModelSearchTerm] = useState("");
  const [isCreatingMake, setIsCreatingMake] = useState(false);
  const [isCreatingModel, setIsCreatingModel] = useState(false);
  const [makesKey, setMakesKey] = useState(0);
  const [modelsKey, setModelsKey] = useState(0);

  // Service Provider state
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [providerSearchTerm, setProviderSearchTerm] = useState("");
  const [isCreatingProvider, setIsCreatingProvider] = useState(false);
  const [providersKey, setProvidersKey] = useState(0);

  // Fetch providers on mount and when dialog opens
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const res = await serviceProviderService.getAllServiceProviders();
        setProviders(res || []);
      } catch (err) {
        console.error("Error fetching providers:", err);
      }
    };

    if (open) {
      fetchProviders();
    }
  }, [open]);

  // Provider filtering
  const filteredProviders = useMemo(() => {
    if (!providerSearchTerm.trim()) {
      return providers;
    }
    const searchLower = providerSearchTerm.toLowerCase();
    return providers.filter((provider) =>
      provider.name.toLowerCase().includes(searchLower)
    );
  }, [providers, providerSearchTerm]);

  const shouldShowAddProviderOption = useMemo(() => {
    if (!providerSearchTerm.trim()) {
      return false;
    }
    const searchLower = providerSearchTerm.toLowerCase().trim();
    return !providers.some(
      (provider) => provider.name.toLowerCase() === searchLower
    );
  }, [providers, providerSearchTerm]);

  const filteredMakes = useMemo(() => {
    if (!makeSearchTerm.trim()) {
      return makes;
    }
    const searchLower = makeSearchTerm.toLowerCase();
    return makes.filter((make) =>
      make.name.toLowerCase().includes(searchLower)
    );
  }, [makes, makeSearchTerm]);

  const shouldShowAddMakeOption = useMemo(() => {
    if (!makeSearchTerm.trim()) {
      return false;
    }
    const searchLower = makeSearchTerm.toLowerCase().trim();
    return !makes.some((make) => make.name.toLowerCase() === searchLower);
  }, [makes, makeSearchTerm]);

  const selectedMake = useMemo(() => {
    if (!formData.makeName) {
      return null;
    }
    return makes.find((make) => make.name === formData.makeName) || null;
  }, [makes, formData.makeName]);

  const filteredModels = useMemo(() => {
    if (!selectedMake) {
      return [];
    }
    let modelsForMake = models.filter(
      (model) => model.makeId === selectedMake.id
    );

    if (!modelSearchTerm.trim()) {
      return modelsForMake;
    }
    const searchLower = modelSearchTerm.toLowerCase();
    return modelsForMake.filter((model) =>
      model.name.toLowerCase().includes(searchLower)
    );
  }, [models, selectedMake, modelSearchTerm]);

  const shouldShowAddModelOption = useMemo(() => {
    if (!selectedMake || !modelSearchTerm.trim()) {
      return false;
    }
    const searchLower = modelSearchTerm.toLowerCase().trim();
    return !filteredModels.some(
      (model) => model.name.toLowerCase() === searchLower
    );
  }, [filteredModels, modelSearchTerm, selectedMake]);

  const hasNoModelsForMake = useMemo(() => {
    if (!selectedMake) {
      return false;
    }
    return (
      models.filter((model) => model.makeId === selectedMake.id).length === 0
    );
  }, [models, selectedMake]);

  const updateField = <K extends keyof CustomerFormData>(
    field: K,
    value: CustomerFormData[K]
  ) => {
    if (field === "makeName") {
      const newData = { ...formData, [field]: value, modelName: undefined };
      setFormData(newData);
      setModelSearchTerm("");
    } else {
      const newData = { ...formData, [field]: value };
      setFormData(newData);
      // Clear phone error when user types, without updating parent formData
      if (field === "phone" && phoneError && onPhoneErrorClear) {
        onPhoneErrorClear();
      }
    }
  };

  const handleCreateMake = async () => {
    if (!makeSearchTerm.trim() || isCreatingMake) {
      return;
    }

    try {
      setIsCreatingMake(true);
      const newMake = await makeService.createMake({
        name: makeSearchTerm.trim(),
      });

      toast.success(`Make "${newMake.name}" created successfully`);

      // Refresh the makes list from parent
      await onMakesRefresh();

      // Force Select to re-render by updating the key
      setMakesKey((prev) => prev + 1);

      // Clear search and select the new make
      setMakeSearchTerm("");
      const newData = {
        ...formData,
        makeName: newMake.name,
        modelName: undefined,
      };
      setFormData(newData);
      if (onFormDataChange) {
        onFormDataChange(newData);
      }
      setModelSearchTerm("");
    } catch (err: any) {
      console.error("Error creating make:", err);
      toast.error(
        err?.response?.data?.message ||
          "Failed to create make. Please try again."
      );
    } finally {
      setIsCreatingMake(false);
    }
  };

  const handleCreateModel = async () => {
    if (!modelSearchTerm.trim() || !selectedMake || isCreatingModel) {
      return;
    }

    try {
      setIsCreatingModel(true);
      const newModel = await modelService.createModel({
        name: modelSearchTerm.trim(),
        makeId: selectedMake.id,
      });

      toast.success(`Model "${newModel.name}" created successfully`);

      // Refresh the models list from parent
      await onModelsRefresh();

      // Force Select to re-render by updating the key
      setModelsKey((prev) => prev + 1);

      // Clear search and select the new model
      setModelSearchTerm("");
      const newData = { ...formData, modelName: newModel.name };
      setFormData(newData);
      if (onFormDataChange) {
        onFormDataChange(newData);
      }
    } catch (err: any) {
      console.error("Error creating model:", err);
      toast.error(
        err?.response?.data?.message ||
          "Failed to create model. Please try again."
      );
    } finally {
      setIsCreatingModel(false);
    }
  };

  const handleCreateProvider = async () => {
    if (!providerSearchTerm.trim() || isCreatingProvider) {
      return;
    }

    try {
      setIsCreatingProvider(true);
      const newProvider = await serviceProviderService.createServiceProvider({
        name: providerSearchTerm.trim(),
      });

      toast.success(
        `Service Provider "${newProvider.name}" created successfully`
      );

      // Refresh the providers list
      const updatedProviders =
        await serviceProviderService.getAllServiceProviders();
      setProviders(updatedProviders || []);

      // Force Select to re-render by updating the key
      setProvidersKey((prev) => prev + 1);

      // Clear search and set the new provider as selected
      setProviderSearchTerm("");
      const newData = {
        ...formData,
        serviceProviderId: newProvider.id,
      };
      setFormData(newData);
      if (onFormDataChange) {
        onFormDataChange(newData);
      }
    } catch (err: any) {
      console.error("Error creating provider:", err);
      toast.error(
        err?.response?.data?.message ||
          "Failed to create service provider. Please try again."
      );
    } finally {
      setIsCreatingProvider(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "add" ? "Add New Customer" : "Edit Customer"}
          </DialogTitle>
          <DialogDescription>
            {mode === "add"
              ? "Enter customer details. Fields marked with * are required."
              : "Update customer details."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => updateField("name", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">Mobile # *</Label>
              <Input
                id="phone"
                placeholder="+974 XXXX XXXX"
                value={formData.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
                aria-invalid={!!phoneError}
                className={phoneError ? "border-destructive" : ""}
              />
              {phoneError && (
                <p className="text-sm text-destructive">{phoneError}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => updateField("email", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="address">Address / Location</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => updateField("address", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => updateField("city", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
              />
            </div>
          </div>

          {/* Customer Details Section */}
          <Separator className="my-2" />
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2">Customer Details</h3>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes || ""}
                onChange={(e) => updateField("notes", e.target.value)}
                rows={3}
                placeholder="Additional notes about the customer..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="customerType">Customer Type</Label>
                <Select
                  value={formData.customerType || ""}
                  onValueChange={(value) =>
                    updateField("customerType", value as CustomerType)
                  }
                >
                  <SelectTrigger id="customerType">
                    <SelectValue placeholder="Select customer type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="subscription-customer">
                      Subscription Customer
                    </SelectItem>
                    <SelectItem value="online-customer">
                      Online Customer
                    </SelectItem>
                    <SelectItem value="offline-customer">
                      Offline Customer
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="customerCategory">Customer Category</Label>
                <Select
                  value={formData.customerCategory || ""}
                  onValueChange={(value) =>
                    updateField("customerCategory", value as CustomerCategory)
                  }
                >
                  <SelectTrigger id="customerCategory">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="occasional-customer">
                      Occasional Customer
                    </SelectItem>
                    <SelectItem value="regular-customer">
                      Regular Customer
                    </SelectItem>
                    <SelectItem value="loyal-customer">
                      Loyal Customer
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="customerStatus">Status</Label>
                <Select
                  value={formData.customerStatus || ""}
                  onValueChange={(value) =>
                    updateField("customerStatus", value as CustomerStatus)
                  }
                >
                  <SelectTrigger id="customerStatus">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="vacation">Vacation</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="serviceProviderId">Service Provider</Label>
              <Select
                key={`provider-select-${providersKey}-${providers.length}`}
                value={formData.serviceProviderId || ""}
                onValueChange={(value) => {
                  updateField("serviceProviderId", value);
                  setProviderSearchTerm("");
                }}
              >
                <SelectTrigger id="serviceProviderId">
                  <SelectValue placeholder="Select service provider">
                    {formData.serviceProviderId
                      ? providers.find(
                          (p) => p.id === formData.serviceProviderId
                        )?.name || "Select service provider"
                      : "Select service provider"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent
                  className="overflow-y-auto max-h-[300px]"
                  key={`provider-content-${providersKey}-${providers.length}`}
                >
                  <div
                    className="sticky top-0 z-10 bg-popover border-b px-1 py-2"
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search provider..."
                        value={providerSearchTerm}
                        onChange={(e) => setProviderSearchTerm(e.target.value)}
                        className="pl-8 h-9"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  {filteredProviders.length > 0
                    ? filteredProviders.map((provider) => (
                        <SelectItem key={provider.id} value={provider.id}>
                          {provider.name}
                        </SelectItem>
                      ))
                    : !shouldShowAddProviderOption && (
                        <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                          No providers found
                        </div>
                      )}
                  {shouldShowAddProviderOption && (
                    <div
                      className="px-2 py-2 border-t cursor-pointer hover:bg-accent"
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        handleCreateProvider();
                      }}
                    >
                      <div className="flex items-center gap-2 text-sm text-primary">
                        <span>+</span>
                        <span>
                          Add "{providerSearchTerm.trim()}" as new provider
                        </span>
                      </div>
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Recurring Appointments Section - Only show in edit mode */}
            {mode === "edit" && customerId && (
              <RepeatedAppointmentSetup
                customerId={customerId}
                customerVehicles={customerVehicles || []}
                packages={oneTimePackages}
              />
            )}
          </div>

          {/* Vehicle Section - Only show in add mode */}
          {mode === "add" && (
            <>
              <Separator className="my-2" />
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-2">
                    Vehicle Details (Optional)
                  </h3>
                  <p className="text-xs text-muted-foreground mb-4">
                    Add a vehicle for this customer. You can add more vehicles
                    later.
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="vehiclePlateNumber">Plate Number</Label>
                  <Input
                    id="vehiclePlateNumber"
                    value={formData.vehiclePlateNumber || ""}
                    onChange={(e) =>
                      updateField("vehiclePlateNumber", e.target.value)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="vehicleMake">Make</Label>
                    <Select
                      key={`make-select-${makesKey}-${makes.length}`}
                      value={
                        formData.makeName
                          ? makes.find((m) => m.name === formData.makeName)
                              ?.id || ""
                          : ""
                      }
                      onValueChange={(value) => {
                        const selectedMake = makes.find((m) => m.id === value);
                        if (selectedMake) {
                          updateField("makeName", selectedMake.name);
                        }
                        setMakeSearchTerm("");
                      }}
                    >
                      <SelectTrigger id="vehicleMake" className="w-full">
                        <SelectValue placeholder="Select make" />
                      </SelectTrigger>
                      <SelectContent
                        className="overflow-y-auto max-h-[300px]"
                        key={`make-content-${makesKey}-${makes.length}`}
                      >
                        <div
                          className="sticky top-0 z-10 bg-popover border-b px-1 py-2"
                          onPointerDown={(e) => e.stopPropagation()}
                        >
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              placeholder="Search make..."
                              value={makeSearchTerm}
                              onChange={(e) =>
                                setMakeSearchTerm(e.target.value)
                              }
                              className="pl-8 h-9"
                              onPointerDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        {filteredMakes.length > 0
                          ? filteredMakes.map((make) => (
                              <SelectItem key={make.id} value={make.id}>
                                {make.name}
                              </SelectItem>
                            ))
                          : !shouldShowAddMakeOption && (
                              <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                                No makes found
                              </div>
                            )}
                        {shouldShowAddMakeOption && (
                          <div
                            className="px-2 py-2 border-t cursor-pointer hover:bg-accent"
                            onPointerDown={(e) => {
                              e.stopPropagation();
                              handleCreateMake();
                            }}
                          >
                            <div className="flex items-center gap-2 text-sm text-primary">
                              <span>+</span>
                              <span>
                                Add "{makeSearchTerm.trim()}" as new make
                              </span>
                            </div>
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="vehicleModel">Model</Label>
                    <Select
                      key={`model-select-${modelsKey}-${models.length}-${selectedMake?.id || ""}`}
                      value={
                        formData.modelName
                          ? filteredModels.find(
                              (m) => m.name === formData.modelName
                            )?.id || ""
                          : ""
                      }
                      onValueChange={(value) => {
                        const selectedModel = filteredModels.find(
                          (m) => m.id === value
                        );
                        if (selectedModel) {
                          updateField("modelName", selectedModel.name);
                        }
                        setModelSearchTerm("");
                      }}
                      disabled={!formData.makeName}
                    >
                      <SelectTrigger id="vehicleModel" className="w-full">
                        <SelectValue
                          placeholder={
                            !formData.makeName
                              ? "Select make first"
                              : hasNoModelsForMake
                                ? "Enter new model"
                                : "Select model"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent
                        className="overflow-y-auto max-h-[300px]"
                        key={`model-content-${modelsKey}-${models.length}-${selectedMake?.id || ""}`}
                      >
                        <div
                          className="sticky top-0 z-10 bg-popover border-b px-1 py-2"
                          onPointerDown={(e) => e.stopPropagation()}
                        >
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              placeholder={
                                hasNoModelsForMake
                                  ? "Enter new model"
                                  : "Search model..."
                              }
                              value={modelSearchTerm}
                              onChange={(e) =>
                                setModelSearchTerm(e.target.value)
                              }
                              className="pl-8 h-9"
                              disabled={!formData.makeName}
                              onPointerDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        {filteredModels.length > 0
                          ? filteredModels.map((model) => (
                              <SelectItem key={model.id} value={model.id}>
                                {model.name}
                              </SelectItem>
                            ))
                          : !shouldShowAddModelOption && (
                              <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                                {formData.makeName
                                  ? "No models found"
                                  : "Select a make first"}
                              </div>
                            )}
                        {shouldShowAddModelOption && (
                          <div
                            className="px-2 py-2 border-t cursor-pointer hover:bg-accent"
                            onPointerDown={(e) => {
                              e.stopPropagation();
                              handleCreateModel();
                            }}
                          >
                            <div className="flex items-center gap-2 text-sm text-primary">
                              <span>+</span>
                              <span>
                                Add "{modelSearchTerm.trim()}" as new model
                              </span>
                            </div>
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="vehicleYear">Year</Label>
                    <Input
                      id="vehicleYear"
                      type="number"
                      value={formData.vehicleYear || ""}
                      onChange={(e) =>
                        updateField(
                          "vehicleYear",
                          e.target.value ? parseInt(e.target.value) : undefined
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                        }
                      }}
                    />
                  </div>
                  <div className="grid gap-2 w-full">
                    <Label htmlFor="vehicleColor">Color</Label>
                    <Select
                      value={formData.vehicleColor || ""}
                      onValueChange={(value) =>
                        updateField("vehicleColor", value)
                      }
                    >
                      <SelectTrigger id="vehicleColor" className="w-full">
                        <SelectValue placeholder="Select color" />
                      </SelectTrigger>
                      <SelectContent className="overflow-y-auto max-h-[200px]">
                        <SelectItem value="white">White</SelectItem>
                        <SelectItem value="black">Black</SelectItem>
                        <SelectItem value="silver">Silver</SelectItem>
                        <SelectItem value="gray">Gray</SelectItem>
                        <SelectItem value="red">Red</SelectItem>
                        <SelectItem value="blue">Blue</SelectItem>
                        <SelectItem value="green">Green</SelectItem>
                        <SelectItem value="brown">Brown</SelectItem>
                        <SelectItem value="beige">Beige</SelectItem>
                        <SelectItem value="gold">Gold</SelectItem>
                        <SelectItem value="orange">Orange</SelectItem>
                        <SelectItem value="yellow">Yellow</SelectItem>
                        <SelectItem value="purple">Purple</SelectItem>
                        <SelectItem value="burgundy">Burgundy</SelectItem>
                        <SelectItem value="maroon">Maroon</SelectItem>
                        <SelectItem value="navy">Navy</SelectItem>
                        <SelectItem value="teal">Teal</SelectItem>
                        <SelectItem value="pearl">Pearl</SelectItem>
                        <SelectItem value="champagne">Champagne</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2 w-full">
                    <Label htmlFor="vehicleType">Vehicle Type</Label>
                    <Select
                      value={formData.vehicleType || ""}
                      onValueChange={(value: VehicleType) =>
                        updateField("vehicleType", value)
                      }
                    >
                      <SelectTrigger id="vehicleType" className="w-full">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sedan">Sedan</SelectItem>
                        <SelectItem value="suv">SUV</SelectItem>
                        <SelectItem value="truck">Truck</SelectItem>
                        <SelectItem value="van">Van</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setFormData(initialFormData); // Reset to initial state
              onCancel();
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              // Pass formData directly to onSave
              onSave(formData);
            }}
          >
            {mode === "add" ? "Add Customer" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
